// 20: "חיבור Gmail" — drawer זמני מכל נתיב, כמו הקונסנט: ?mail=send | read | send,read (צריך לחבר עכשיו) | manage (מהתפריט).
// נפתח כשאייבי/יצירה נתקלים ב-no_mailbox (אירוע mail_connect בשיחה, mailConnect בריצה), ונסגר כשמסיימים או מתי שרוצים.
// החלון של גוגל נפתח כחלון קופץ; ה-callback (/mail/connected) מודיע ב-BroadcastChannel (עובד גם כש-COOP של גוגל ניתק את
// window.opener) וב-postMessage ל-opener באותו origin, ונסגר. ה-drawer גם בודק את החשבון כל 2 שניות — גיבוי לשניהם.
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import type { MailCap } from './mailApi.ts';

export const MAIL_PARAM = 'mail';
/** חזרה באותה לשונית (בלי חלון קופץ): connected | <קוד שגיאה>. */
export const MAIL_RESULT_PARAM = 'mailResult';
export const MAIL_CHANNEL = 'aibify-mail';
export const MAIL_MSG = 'aibify:mail';

export type MailMsg = { type: typeof MAIL_MSG; ok: true; caps: MailCap[] } | { type: typeof MAIL_MSG; ok: false; error: string };
export type MailMode = { mode: 'need'; caps: MailCap[] } | { mode: 'manage' };

const CAPS = new Set<string>(['send', 'read']);
export function parseCaps(v: string | null | undefined): MailCap[] {
  const out = [...new Set(String(v ?? '').split(',').map((x) => x.trim()).filter((x) => CAPS.has(x)))] as MailCap[];
  return out.sort((a, b) => (a === 'send' ? -1 : b === 'send' ? 1 : 0));
}
export function parseMailParam(v: string | null): MailMode | null {
  if (!v) return null;
  if (v === 'manage') return { mode: 'manage' };
  const caps = parseCaps(v);
  return caps.length ? { mode: 'need', caps } : { mode: 'manage' };
}

export function isMailMsg(d: unknown): d is MailMsg {
  const m = d as Partial<MailMsg> | null;
  return !!m && m.type === MAIL_MSG && typeof m.ok === 'boolean';
}

/** פותח את ה-drawer: caps = מה שצריך עכשיו; בלי — מצב ניהול (מהתפריט). */
export function useMailConnect() {
  const [, setSp] = useSearchParams();
  return useCallback(
    (caps?: MailCap[]) =>
      setSp((p) => {
        const n = new URLSearchParams(p);
        n.set(MAIL_PARAM, caps?.length ? parseCaps(caps.join(',')).join(',') : 'manage');
        n.delete(MAIL_RESULT_PARAM);
        return n;
      }),
    [setSp],
  );
}

/* ── המשך אחרי חיבור: מי שנחסם (ריצה, תור של אייבי) נרשם; ה-drawer מודיע ויודע אם יש מי שממשיך ── */

type Resumer = (caps: MailCap[]) => void;
const resumers = new Set<Resumer>();
export const hasResumers = () => resumers.size > 0;
export function announceConnected(caps: MailCap[]) {
  for (const r of [...resumers]) r(caps);
}
/** cb רץ פעם אחת לכל חיבור מוצלח בזמן שהקומפוננטה רשומה (enabled). */
export function useOnMailConnected(cb: Resumer, enabled = true) {
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => {
    if (!enabled) return;
    const r: Resumer = (caps) => ref.current(caps);
    resumers.add(r);
    return () => void resumers.delete(r);
  }, [enabled]);
}

/** דף ה-callback בחלון הקופץ: מודיע ל-drawer בחלון שפתח אותו. */
export function notifyOpener(msg: MailMsg) {
  try {
    const ch = new BroadcastChannel(MAIL_CHANNEL);
    ch.postMessage(msg);
    ch.close();
  } catch {
    /* דפדפן בלי BroadcastChannel — postMessage/polling */
  }
  try {
    if (window.opener && window.opener !== window) (window.opener as Window).postMessage(msg, window.location.origin);
  } catch {
    /* opener ממקור אחר / נותק */
  }
}

/** נתיב החזרה לחיבור באותה לשונית: הדף הנוכחי עם ?mail=… (בלי תוצאה קודמת). */
export function returnPathFor(loc: { pathname: string; search: string }, modeParam: string) {
  const n = new URLSearchParams(loc.search);
  n.set(MAIL_PARAM, modeParam);
  n.delete(MAIL_RESULT_PARAM);
  return `${loc.pathname}?${n}`;
}
